import Image from 'next/image';
import Markdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { Icon } from '@/components/ui/icon';
import VisibleLoading from '@/components/utilities/VisibleLoading';
import ComponentsRender from '@/components/ComponentsRender';
import YoutubeVideo from '@/components/utilities/YoutubeVideo';

// constants
import { COMPONENTS } from '@/components/components';

// utilities
import { getImagePath } from '@/utils/images';

const COLORS = {
  brand: 'bg-brand-500',
  purple: 'bg-purple-500',
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  blue: 'bg-blue-500',
  red: 'bg-red-500'
};

const HeroCampaignBlock = ({
  variant,
  icon,
  iconColor,
  title,
  titleFontSizeMobile,
  titleFontSize,
  titleLayout,
  text,
  image,
  videoId,
  buttons,
  heroCampaignBlockContent: content
}) => {
  const isInline = titleLayout !== 'stacked';
  const colorClassName = COLORS[iconColor] || 'bg-brand-500';

  return (
    <div className="relative w-full">
      <section
        data-variant="light"
        className="relative overflow-hidden rounded-b-4xl bg-white pt-32 pb-10 text-black 2xl:overflow-x-auto"
      >
        {variant === 'title_image_row' ? (
          <>
            <div className="relative z-3 xl:container xl:mx-auto">
              <div className="mx-auto mb-10 w-[90vw] xl:w-(--container-width)">
                <div className="my-16 flex flex-wrap items-center justify-center gap-16 xl:my-32">
                  <div className="relative z-2">
                    {title && (
                      <VisibleLoading delay={75}>
                        {({ ref, className }) => (
                          <div
                            ref={ref}
                            className={`mb-4 flex flex-col items-center justify-center gap-2 ${isInline ? 'xl:flex-row xl:gap-6' : ''} ${className || ''}`.trim()}
                          >
                            <div
                              className={`flex h-12 w-12 items-center justify-center rounded-full text-white ${colorClassName}`}
                            >
                              <Icon type={icon} width={28} height={28} />
                            </div>
                            <h1
                              className={`max-w-[335px] justify-center text-center font-normal xl:max-w-[800px] ${className || ''}`.trim()}
                              style={{
                                ...(titleFontSizeMobile ? { fontSize: `${titleFontSizeMobile}px` } : {}),
                                ...(titleFontSize ? { '--title-font-size-lg': `${titleFontSize}px` } : {})
                              }}
                            >
                              {title}
                            </h1>
                          </div>
                        )}
                      </VisibleLoading>
                    )}

                    {text && (
                      <VisibleLoading delay={150}>
                        {({ ref, className }) => (
                          <div
                            ref={ref}
                            className={`txt-xl text-grey-700 mx-auto mb-4 max-w-[335px] whitespace-pre-line text-center xl:mx-0 xl:max-w-[544px] xl:text-left [&_a]:underline ${className || ''}`.trim()}
                          >
                            <Markdown
                              components={{
                                a: ({ href, children }) => {
                                  const isExternal = href && !href.startsWith('/') && !href.startsWith('#');
                                  return (
                                    <a href={href} {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                                      {children}
                                    </a>
                                  );
                                }
                              }}
                            >
                              {text}
                            </Markdown>
                          </div>
                        )}
                      </VisibleLoading>
                    )}

                    {(buttons || []).length > 0 && (
                      <VisibleLoading delay={300}>
                        {({ ref, className }) => (
                          <div
                            ref={ref}
                            className={`mx-auto my-10 flex flex-wrap justify-center gap-2 xl:mx-0 xl:my-6 xl:justify-start ${className || ''}`.trim()}
                          >
                            {buttons.map((button, index) => (
                              <VisibleLoading key={button.id} delay={index * 100}>
                                {({ ref, className }) => (
                                  <Button
                                    ref={ref}
                                    key={button.id}
                                    variant={button.variant}
                                    size={button.size}
                                    asChild={true}
                                    className={`basis-full xl:basis-auto ${className || ''}`.trim()}
                                  >
                                    <Link href={button.url || ''}>
                                      <Icon type={button.icon} /> {button.label}
                                    </Link>
                                  </Button>
                                )}
                              </VisibleLoading>
                            ))}
                          </div>
                        )}
                      </VisibleLoading>
                    )}
                  </div>
                  <div className="relative z-1 xl:min-w-[490px]">
                    <VisibleLoading delay={500} direction="left">
                      {({ ref, className }) => (
                        <div
                          ref={ref}
                          className={`gpu-render absolute top-1/4 right-0 h-[200px] w-[400px] lg:block ${className}`}
                        >
                          <div className="absolute h-full w-full rounded-full bg-blue-500 blur-[200px]" />
                          <svg
                            className="absolute hidden h-[calc(200px*2+200px*2)] w-[calc(400px*2+200px*2)] -translate-x-1/2 -translate-y-1/2 xl:block"
                            width="100%"
                            height="100%"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <defs>
                              <pattern id="lines" patternUnits="userSpaceOnUse" width="48" height="48">
                                <line x1="0" y1="0" x2="48" y2="0" stroke="white" strokeWidth="1" />
                              </pattern>
                            </defs>
                            <rect width="100%" height="100%" fill="url(#lines)" />
                          </svg>
                        </div>
                      )}
                    </VisibleLoading>
                    {image && (
                      <VisibleLoading delay={150}>
                        {({ ref, className }) => (
                          <div ref={ref} className={`relative z-3 ${className || ''}`.trim()}>
                            <Image
                              src={getImagePath(image)}
                              width={image.width}
                              height={image.height}
                              alt={image.alternativeText}
                              className={`mx-auto max-h-[380px] w-full min-w-[200px] rounded-3xl object-cover shadow-2xl/15`.trim()}
                            />
                          </div>
                        )}
                      </VisibleLoading>
                    )}

                    <VisibleLoading delay={800} direction="left">
                      {({ ref, className }) => (
                        <div
                          ref={ref}
                          className={`absolute -top-[29px] left-1/8 z-2 hidden h-[24px] w-[24px] rounded-full bg-blue-500 lg:block ${className || ''}`.trim()}
                        />
                      )}
                    </VisibleLoading>

                    <VisibleLoading delay={1000} direction="right">
                      {({ ref, className }) => (
                        <div
                          ref={ref}
                          className={`bg-brand-400 absolute top-[68px] -right-[20px] z-2 hidden h-[24px] w-[24px] rounded-full lg:block ${className || ''}`.trim()}
                        />
                      )}
                    </VisibleLoading>

                    <VisibleLoading delay={1200} direction="left">
                      {({ ref, className }) => (
                        <div
                          ref={ref}
                          className={`absolute bottom-[35px] -left-[40px] z-4 hidden h-[57px] w-[57px] rounded-full bg-yellow-400 lg:block ${className || ''}`.trim()}
                        />
                      )}
                    </VisibleLoading>
                  </div>
                </div>

                {(content || []).map((component) => (
                  <ComponentsRender
                    key={`${component.id}_${component.__typename}`}
                    type={COMPONENTS}
                    component={component}
                  />
                ))}
              </div>
            </div>
          </>
        ) : variant === 'title_image_col' ? (
          <>
            <VisibleLoading delay={500} direction="left">
              {({ ref, className }) => (
                <div
                  ref={ref}
                  className={`gpu-render absolute top-[500px] left-[200px] hidden h-[400px] w-[400px] lg:block ${className}`}
                >
                  <div className="absolute h-full w-full rounded-full bg-purple-500 blur-[200px]" />
                  <svg
                    className="absolute h-[calc(400px*2+200px*2)] w-[calc(400px*2+200px*2)] -translate-x-1/2 -translate-y-1/2"
                    width="100%"
                    height="100%"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <pattern id="lines" patternUnits="userSpaceOnUse" width="48" height="48">
                        <line x1="0" y1="0" x2="48" y2="0" stroke="white" strokeWidth="1" />
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#lines)" />
                  </svg>
                </div>
              )}
            </VisibleLoading>

            <VisibleLoading delay={500} direction="left">
              {({ ref, className }) => (
                <div
                  ref={ref}
                  className={`gpu-render absolute top-[404px] right-[300px] hidden h-[400px] w-[400px] lg:block ${className}`}
                >
                  <div className="absolute h-full w-full rounded-full bg-green-500 blur-[200px]" />
                  <svg
                    className="absolute h-[calc(400px*2+200px*2)] w-[calc(400px*2+200px*2)] -translate-x-1/2 -translate-y-1/2"
                    width="100%"
                    height="100%"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <pattern id="lines" patternUnits="userSpaceOnUse" width="48" height="48">
                        <line x1="0" y1="0" x2="48" y2="0" stroke="white" strokeWidth="1" />
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#lines)" />
                  </svg>
                </div>
              )}
            </VisibleLoading>

            <VisibleLoading delay={500} direction="left">
              {({ ref, className }) => (
                <div
                  ref={ref}
                  className={`gpu-render absolute top-[740px] right-1/2 hidden h-[400px] w-[400px] translate-x-1/2 lg:block ${className}`}
                >
                  <div className="absolute h-full w-full rounded-full bg-yellow-500 blur-[200px]" />
                  <svg
                    className="absolute h-[calc(400px*2+200px*2)] w-[calc(400px*2+200px*2)] -translate-x-1/2 -translate-y-1/2"
                    width="100%"
                    height="100%"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <pattern id="lines" patternUnits="userSpaceOnUse" width="48" height="48">
                        <line x1="0" y1="0" x2="48" y2="0" stroke="white" strokeWidth="1" />
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#lines)" />
                  </svg>
                </div>
              )}
            </VisibleLoading>

            <VisibleLoading delay={700} direction="left">
              {({ ref, className }) => (
                <div
                  ref={ref}
                  className={`absolute top-[364px] left-1/4 z-2 hidden h-[31px] w-[31px] rounded-full bg-purple-500 lg:block ${className || ''}`.trim()}
                />
              )}
            </VisibleLoading>

            <VisibleLoading delay={1000} direction="left">
              {({ ref, className }) => (
                <div
                  ref={ref}
                  className={`bg-brand-400 absolute top-[700px] left-1/8 z-2 hidden h-[31px] w-[31px] rounded-full lg:block ${className || ''}`.trim()}
                />
              )}
            </VisibleLoading>

            <VisibleLoading delay={900} direction="right">
              {({ ref, className }) => (
                <div
                  ref={ref}
                  className={`absolute top-[605px] left-7/8 z-2 hidden h-[31px] w-[31px] rounded-full bg-green-500 lg:block ${className || ''}`.trim()}
                />
              )}
            </VisibleLoading>

            <div className="relative z-3 xl:container xl:mx-auto">
              <div className="mx-auto mb-10 w-[90vw] xl:w-(--container-width)">
                {title && (
                  <VisibleLoading delay={75}>
                    {({ ref, className }) => (
                      <div
                        ref={ref}
                        className={`mb-4 flex flex-col items-center justify-center gap-2 ${isInline ? 'xl:flex-row xl:gap-6' : ''} ${className || ''}`.trim()}
                      >
                        <div
                          className={`flex h-12 w-12 items-center justify-center rounded-full text-white ${colorClassName}`}
                        >
                          <Icon type={icon} width={28} height={28} />
                        </div>
                        <h1
                          className={`max-w-[335px] justify-center text-center font-normal xl:max-w-[800px] ${className || ''}`.trim()}
                          style={{
                            ...(titleFontSizeMobile ? { fontSize: `${titleFontSizeMobile}px` } : {}),
                            ...(titleFontSize ? { '--title-font-size-lg': `${titleFontSize}px` } : {})
                          }}
                        >
                          {title}
                        </h1>
                      </div>
                    )}
                  </VisibleLoading>
                )}

                {text && (
                  <VisibleLoading delay={150}>
                    {({ ref, className }) => (
                      <div
                        ref={ref}
                        className={`txt-xl text-grey-700 mx-auto mb-4 max-w-[335px] whitespace-pre-line text-center xl:max-w-[544px] [&_a]:underline ${className || ''}`.trim()}
                      >
                        <Markdown
                          components={{
                            a: ({ href, children }) => {
                              const isExternal = href && !href.startsWith('/') && !href.startsWith('#');
                              return (
                                <a href={href} {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                                  {children}
                                </a>
                              );
                            }
                          }}
                        >
                          {text}
                        </Markdown>
                      </div>
                    )}
                  </VisibleLoading>
                )}

                {(buttons || []).length > 0 && (
                  <VisibleLoading delay={300}>
                    {({ ref, className }) => (
                      <div
                        ref={ref}
                        className={`mx-auto my-10 flex flex-wrap justify-center gap-2 xl:my-6 ${className || ''}`.trim()}
                      >
                        {buttons.map((button, index) => (
                          <VisibleLoading key={button.id} delay={index * 100}>
                            {({ ref, className }) => (
                              <Button
                                ref={ref}
                                key={button.id}
                                variant={button.variant}
                                size={button.size}
                                asChild={true}
                                className={`basis-full xl:basis-auto ${className || ''}`.trim()}
                              >
                                <Link href={button.url || ''}>
                                  <Icon type={button.icon} /> {button.label}
                                </Link>
                              </Button>
                            )}
                          </VisibleLoading>
                        ))}
                      </div>
                    )}
                  </VisibleLoading>
                )}

                {videoId ? (
                  <VisibleLoading delay={150}>
                    {({ ref, className }) => (
                      <div ref={ref} className={`my-10 xl:my-32 ${className || ''}`.trim()}>
                        <div className="border-white-75 mx-auto h-[250px] w-full min-w-[200px] overflow-hidden rounded-3xl border border-8 xl:h-[695px]">
                          <YoutubeVideo
                            videoId={videoId}
                            thumbnail={
                              <Image
                                src={getImagePath(image)}
                                alt={image.alternativeText || title}
                                fill={true}
                                className="object-cover"
                              />
                            }
                          />
                        </div>
                      </div>
                    )}
                  </VisibleLoading>
                ) : image ? (
                  <VisibleLoading delay={150}>
                    {({ ref, className }) => (
                      <div ref={ref} className={`my-10 xl:my-32 ${className || ''}`.trim()}>
                        <Image
                          src={getImagePath(image)}
                          width={image.width}
                          height={image.height}
                          alt={image.alternativeText}
                          className={`border-white-75 mx-auto max-h-[640px] w-full min-w-[200px] rounded-3xl border border-8 object-cover`.trim()}
                        />
                      </div>
                    )}
                  </VisibleLoading>
                ) : null}

                {(content || []).map((component) => (
                  <ComponentsRender
                    key={`${component.id}_${component.__typename}`}
                    type={COMPONENTS}
                    component={component}
                  />
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="my-16 xl:container xl:mx-auto">
              <div className="mx-auto mb-10 w-[90vw] xl:w-(--container-width)">
                {title && (
                  <VisibleLoading delay={75}>
                    {({ ref, className }) => (
                      <div
                        ref={ref}
                        className={`mb-4 flex flex-col items-center justify-center gap-2 ${isInline ? 'xl:flex-row xl:gap-6' : ''} ${className || ''}`.trim()}
                      >
                        <div
                          className={`flex h-12 w-12 items-center justify-center rounded-full text-white ${colorClassName}`}
                        >
                          <Icon type={icon} width={28} height={28} />
                        </div>
                        <h1
                          className={`max-w-[335px] justify-center text-center font-normal xl:max-w-[800px] ${className || ''}`.trim()}
                          style={{
                            ...(titleFontSizeMobile ? { fontSize: `${titleFontSizeMobile}px` } : {}),
                            ...(titleFontSize ? { '--title-font-size-lg': `${titleFontSize}px` } : {})
                          }}
                        >
                          {title}
                        </h1>
                      </div>
                    )}
                  </VisibleLoading>
                )}

                {text && (
                  <VisibleLoading delay={150}>
                    {({ ref, className }) => (
                      <div
                        ref={ref}
                        className={`txt-xl text-grey-700 mx-auto mb-4 max-w-[335px] whitespace-pre-line text-center xl:max-w-[544px] [&_a]:underline ${className || ''}`.trim()}
                      >
                        <Markdown
                          components={{
                            a: ({ href, children }) => {
                              const isExternal = href && !href.startsWith('/') && !href.startsWith('#');
                              return (
                                <a href={href} {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                                  {children}
                                </a>
                              );
                            }
                          }}
                        >
                          {text}
                        </Markdown>
                      </div>
                    )}
                  </VisibleLoading>
                )}

                {(buttons || []).length > 0 && (
                  <VisibleLoading delay={300}>
                    {({ ref, className }) => (
                      <div
                        ref={ref}
                        className={`mx-auto my-10 flex flex-wrap justify-center gap-2 xl:my-6 ${className || ''}`.trim()}
                      >
                        {buttons.map((button, index) => (
                          <VisibleLoading key={button.id} delay={index * 100}>
                            {({ ref, className }) => (
                              <Button
                                ref={ref}
                                key={button.id}
                                variant={button.variant}
                                size={button.size}
                                asChild={true}
                                className={`basis-full xl:basis-auto ${className || ''}`.trim()}
                              >
                                <Link href={button.url || ''}>
                                  <Icon type={button.icon} /> {button.label}
                                </Link>
                              </Button>
                            )}
                          </VisibleLoading>
                        ))}
                      </div>
                    )}
                  </VisibleLoading>
                )}

                {(content || []).map((component) => (
                  <ComponentsRender
                    key={`${component.id}_${component.__typename}`}
                    type={COMPONENTS}
                    component={component}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
};
export default HeroCampaignBlock;
